import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchWithActivity } from '../utils/fetchWithActivity';
import { PolicyDocument } from '../contexts/DocumentContext';
import { useBackgroundReports } from '../contexts/BackgroundReportContext';
import InsuranceSummaryCreateModal from './InsuranceSummaryCreateModal';
import InsuranceSummaryDetail from './InsuranceSummaryDetail';

export interface InsuranceSummaryRecord {
  id: string;
  title: string;
  content: string;
  documentNames: string[];
  documentIds: string[];
  projectId: string | null;
  workflowType?: string | null;
  primaryPolicyType?: string;
  createdAt: string;
  isOwner?: boolean;
  canDelete?: boolean;
  createdByName?: string;
}

interface Props {
  customerId: string;
  customerName: string;
  projectId: string;
  projectTitle: string;
  documents: PolicyDocument[];
  canEdit: boolean;
  /** If set, auto-open this summary's detail modal once it appears in the list. */
  autoOpenSummaryId?: string | null;
  /** Called after we've consumed autoOpenSummaryId so the parent can clear it. */
  onAutoOpenConsumed?: () => void;
}

const apiBase = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';

const InsuranceSummaryView: React.FC<Props> = ({
  customerId,
  customerName,
  projectId,
  projectTitle,
  documents,
  canEdit,
  autoOpenSummaryId,
  onAutoOpenConsumed,
}) => {
  const { pendingReports, completedReports } = useBackgroundReports();
  const [summaries, setSummaries] = useState<InsuranceSummaryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<InsuranceSummaryRecord | null>(null);

  const projectDocuments = useMemo(
    () => documents.filter((d) => (d as any).projectId === projectId),
    [documents, projectId]
  );

  // Background jobs for THIS project that are still running
  const inFlight = useMemo(
    () =>
      pendingReports.filter(
        (r) => r.workflowType === 'insurance_summary' && r.projectId === projectId && r.status === 'processing'
      ),
    [pendingReports, projectId]
  );

  // Track completed jobs we've already reacted to so we don't refetch on every render
  const reactedToCompletedIdsRef = useRef<Set<string>>(new Set());

  const fetchSummaries = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('riskninja-token');
      const res = await fetchWithActivity(`${apiBase}/api/customers/${customerId}/reports`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setSummaries([]);
        return;
      }
      const all: InsuranceSummaryRecord[] = await res.json();
      const filtered = all.filter(
        (r) => r.workflowType === 'insurance_summary' && r.projectId === projectId
      );
      setSummaries(filtered);
    } catch (err) {
      console.error('Failed to load insurance summaries:', err);
      setSummaries([]);
    } finally {
      setLoading(false);
    }
  }, [customerId, projectId]);

  useEffect(() => {
    fetchSummaries();
  }, [fetchSummaries]);

  // If the parent passed a deep-link summary id (from a notification redirect),
  // open it as soon as it appears in the fetched list.
  useEffect(() => {
    if (!autoOpenSummaryId) return;
    const match = summaries.find((s) => s.id === autoOpenSummaryId);
    if (match) {
      setSelected(match);
      onAutoOpenConsumed?.();
    }
  }, [autoOpenSummaryId, summaries, onAutoOpenConsumed]);

  // When a background insurance_summary job for this project completes, refresh the list.
  useEffect(() => {
    const ourCompleted = completedReports.filter(
      (r) => r.workflowType === 'insurance_summary' && r.projectId === projectId
    );
    let shouldRefresh = false;
    for (const r of ourCompleted) {
      if (!reactedToCompletedIdsRef.current.has(r.workflowId)) {
        reactedToCompletedIdsRef.current.add(r.workflowId);
        shouldRefresh = true;
      }
    }
    if (shouldRefresh) fetchSummaries();
  }, [completedReports, projectId, fetchSummaries]);

  // Also refresh whenever an in-flight job count drops (covers the case where
  // a job moves out of pending faster than its completed snapshot is built).
  const prevInFlightCount = useRef(inFlight.length);
  useEffect(() => {
    if (prevInFlightCount.current > 0 && inFlight.length < prevInFlightCount.current) {
      fetchSummaries();
    }
    prevInFlightCount.current = inFlight.length;
  }, [inFlight.length, fetchSummaries]);

  const handleDelete = async (summary: InsuranceSummaryRecord) => {
    if (!window.confirm(`Delete summary "${summary.title}"? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('riskninja-token');
      const res = await fetchWithActivity(
        `${apiBase}/api/customers/${customerId}/reports/${summary.id}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        setSummaries((prev) => prev.filter((s) => s.id !== summary.id));
      } else {
        alert('Failed to delete summary.');
      }
    } catch (err) {
      console.error(err);
      alert('Failed to delete summary.');
    }
  };

  const lineCountFromContent = (content: string): number => {
    try {
      const parsed = typeof content === 'string' ? JSON.parse(content) : content;
      return Array.isArray(parsed?.lines_of_coverage) ? parsed.lines_of_coverage.length : 0;
    } catch {
      return 0;
    }
  };

  const hasContent = summaries.length > 0 || inFlight.length > 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-secondary dark:text-dark-text">
            Insurance Summaries
          </h3>
          <p className="text-sm text-accent dark:text-dark-muted">
            Versioned snapshots of {customerName} coverage for {projectTitle}.
            {inFlight.length > 0 && (
              <span className="ml-2 text-primary">
                · {inFlight.length} processing
              </span>
            )}
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowCreate(true)}
            disabled={projectDocuments.length === 0}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={projectDocuments.length === 0 ? 'Upload documents to this project first' : ''}
          >
            <svg className="inline mr-2" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
            </svg>
            Create Summary
          </button>
        )}
      </div>

      {loading && !hasContent ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : !hasContent ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400">
              <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M10,17L6,13L7.41,11.59L10,14.17L16.59,7.58L18,9L10,17Z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-secondary dark:text-dark-text mb-2">
            No insurance summaries yet
          </h3>
          <p className="text-accent dark:text-dark-muted mb-4">
            Pick which documents to include and generate a coverage snapshot for this project folder.
          </p>
          {projectDocuments.length === 0 && (
            <p className="text-sm text-amber-600 dark:text-amber-400">
              Upload at least one policy document to this project before creating a summary.
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {inFlight.map((job) => (
            <div
              key={job.workflowId}
              className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4"
            >
              <div className="flex items-start gap-2 mb-3">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 dark:border-blue-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-secondary dark:text-dark-text text-sm leading-tight break-words">
                    Generating summary…
                  </h4>
                  <p className="text-xs text-accent dark:text-dark-muted mt-1">
                    Running in the background. You'll be notified when it's ready.
                  </p>
                </div>
              </div>
              <div className="text-xs text-accent dark:text-dark-muted">
                Started {new Date(job.startedAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                {job.documents?.length ? ` · ${job.documents.length} ${job.documents.length === 1 ? 'document' : 'documents'}` : ''}
              </div>
            </div>
          ))}
          {summaries.map((summary) => {
            const lineCount = lineCountFromContent(summary.content);
            return (
              <div
                key={summary.id}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow group relative cursor-pointer"
                onClick={() => setSelected(summary)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-blue-600 dark:text-blue-400">
                        <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M10,17L6,13L7.41,11.59L10,14.17L16.59,7.58L18,9L10,17Z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-secondary dark:text-dark-text text-sm leading-tight break-words">
                        {summary.title}
                      </h4>
                    </div>
                  </div>
                  {canEdit && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(summary);
                      }}
                      className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 p-1 transition-opacity flex-shrink-0 ml-2"
                      title="Delete summary"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                      </svg>
                    </button>
                  )}
                </div>
                <div className="space-y-1 text-sm text-accent dark:text-dark-muted">
                  <div className="flex items-center gap-2">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9,10H7V12H9V10M13,10H11V12H13V10M17,10H15V12H17V10M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5C3.89,21 3,20.1 3,19V5A2,2 0 0,1 5,3H6V1H8V3H16V1H18V3H19M19,19V8H5V19H19Z" />
                    </svg>
                    {new Date(summary.createdAt).toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}
                  </div>
                  <div>
                    {lineCount} {lineCount === 1 ? 'line' : 'lines'} of coverage · {summary.documentNames.length} {summary.documentNames.length === 1 ? 'document' : 'documents'}
                  </div>
                  {summary.createdByName && (
                    <div className="text-xs">by {summary.createdByName}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <InsuranceSummaryCreateModal
          customerId={customerId}
          projectId={projectId}
          projectTitle={projectTitle}
          customerName={customerName}
          documents={projectDocuments}
          onClose={() => setShowCreate(false)}
          onQueued={() => setShowCreate(false)}
        />
      )}

      {selected && (
        <InsuranceSummaryDetail
          summary={selected}
          customerName={customerName}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};

export default InsuranceSummaryView;
